import {stripHtml} from 'string-strip-html';
import {spotheroGet} from 'api';
import {getTransientPricingAlgorithms} from 'api/rates/rating-rule-groups';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys} from 'utils/format-strings';
import {type RawApiResponse} from 'api/api.types';
import {
    type FacilityAirportRate,
    type FacilityDetails,
    type FacilityEventRate,
    type FacilityMonthlyRate,
    type FacilityTransientRate,
} from 'api/facilities/facilities.types';
import {type TransientPricingAlgorithms} from 'api/rates/rates.types';

export type GetFacilityDetailsResponse = Omit<
    FacilityDetails,
    'transientPricingAlgorithms'
>;

export type GetFacilityDetailsRawResponse =
    RawApiResponse<GetFacilityDetailsResponse>;

export interface GetFacilityDetailsFormattedResponse extends FacilityDetails {
    transientRates: FacilityTransientRate[] | [];
    monthlyRates: FacilityMonthlyRate[] | [];
    eventRates: FacilityEventRate[] | [];
    airportRates: FacilityAirportRate[] | [];
}

/**
 * Formats the facility details response
 *
 * @param  id - The facility id
 * @param transientPricingAlgorithms
 * @param  data - The seller details data for a single facility
 * @returns  The formatted facility details
 *
 * @group DTO Formatters
 */
export const formatFacilityDetailsResponse = (
    id: number,
    transientPricingAlgorithms: TransientPricingAlgorithms[],
    data: GetFacilityDetailsRawResponse
) => {
    const formattedData = camelizeKeys(data);

    const response = {
        id,
        transientPricingAlgorithms,
        ...formattedData,

        transientRates:
            formattedData.transientRates?.map((rate) => ({
                ...rate,
                ends: rate.ends || 'Forever',
                type: 'transient',
            })) || [],
        monthlyRates:
            formattedData.monthlyRates?.map((rate) => ({
                ...rate,
                ends: rate.ends || 'Forever',
                type: 'monthly',
                description: stripHtml(rate.newDescription).result,
            })) || [],
        eventRates:
            formattedData.eventRates?.map((rate) => ({
                ...rate,
                ends: rate.ends || 'Forever',
                type: 'event',
            })) || [],
        airportRates:
            formattedData?.airportRates?.map((rate) => ({
                ...rate,
                starts: rate.startDay,
                ends: rate.endDay || 'Forever',
                type: 'airport',
            })) || [],
    };

    return response;
};

/**
 * Gets the facility details for the given facility id.
 * @param  facilityId - The facility id.
 *
 * @see '/api/v1/facilities/{facilityId}/seller-details/'
 */
const getFacilityDetails = async (facilityId: number) => {
    try {
        const {
            data: {data},
        } = await spotheroGet<GetFacilityDetailsRawResponse>(
            `facilities/${facilityId}/seller-details/`
        );

        const transientPricingAlgorithmsResponse =
            await getTransientPricingAlgorithms([facilityId]);

        const {transientPricingAlgorithms} =
            transientPricingAlgorithmsResponse[facilityId];

        const formattedResponse = formatFacilityDetailsResponse(
            facilityId,
            transientPricingAlgorithms,
            data
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

export default getFacilityDetails;
